import {
  Button,
  Checkbox,
  cn,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  MonthPicker,
  Progress,
  RadioCardGroup,
  RadioCardItem,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export type GraduationType =
  | 'bacharelado'
  | 'doutorado'
  | 'ensino-fundamental'
  | 'ensino-medio'
  | 'licenciatura'
  | 'mestrado'
  | 'pos-graduacao'
  | 'tecnico'
  | 'tecnologo';

export type CourseRequirementLevel = 'qualquer-curso' | 'quero-especificar' | '';

export interface JobEditGraduationValues {
  graduationTypes: GraduationType[];
  dateMin: string;
  dateMax: string;
  courseRequirementLevel: CourseRequirementLevel;
}

export interface JobEditGraduationPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  values: JobEditGraduationValues;
  errors: Record<string, string>;
  /** `true` enquanto o submit do form está em voo. */
  isSubmitting: boolean;
  /**
   * `false` quando há erro de validação cross-field ativo (ex.: data máxima
   * anterior à mínima). Desabilita o botão "Avançar".
   */
  canSubmit: boolean;
  /**
   * EU-569 — vaga Encerrada / Cancelada: modo somente visualização. Os campos
   * ficam desabilitados (fieldset + envelope "Edição bloqueada"), mas
   * "Avançar" segue habilitado — só NAVEGA (o container não persiste).
   */
  readOnly?: boolean;
  progress: number;
  onToggleGraduationType: (value: GraduationType) => void;
  onDateMinChange: (value: string) => void;
  onDateMaxChange: (value: string) => void;
  onCourseRequirementLevelChange: (value: CourseRequirementLevel) => void;
  onBack: () => void;
  /**
   * Submit do form. O container decide o destino: se
   * `courseRequirementLevel === 'quero-especificar'`, navega para
   * `/formacao/cursos`; caso contrário, retorna ao hub `/editar`.
   */
  onSubmit: () => void;
}

const GRADUATION_OPTIONS: readonly { value: GraduationType; label: string }[] = [
  { value: 'bacharelado', label: 'Bacharelado' },
  { value: 'doutorado', label: 'Doutorado' },
  { value: 'ensino-fundamental', label: 'Ensino fundamental' },
  { value: 'ensino-medio', label: 'Ensino médio' },
  { value: 'licenciatura', label: 'Licenciatura' },
  { value: 'mestrado', label: 'Mestrado' },
  { value: 'pos-graduacao', label: 'Pós-Graduação' },
  { value: 'tecnico', label: 'Técnico' },
  { value: 'tecnologo', label: 'Tecnólogo' },
];

const HIGHER_OR_TECHNICAL_TYPES: readonly GraduationType[] = [
  'bacharelado',
  'doutorado',
  'licenciatura',
  'mestrado',
  'pos-graduacao',
  'tecnico',
  'tecnologo',
];

export function isHigherOrTechnical(types: readonly GraduationType[]): boolean {
  return types.some((type) => HIGHER_OR_TECHNICAL_TYPES.includes(type));
}

export function JobEditGraduationPage({
  jobName,
  companyLogoUrl,
  companyName,
  values,
  errors,
  isSubmitting,
  canSubmit,
  readOnly = false,
  progress,
  onToggleGraduationType,
  onDateMinChange,
  onDateMaxChange,
  onCourseRequirementLevelChange,
  onBack,
  onSubmit,
}: JobEditGraduationPageProps) {
  const showCourseLevel = isHigherOrTechnical(values.graduationTypes);

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Formação
            </p>
          </header>

          <Progress value={progress} className="w-full max-w-[630px] bg-secondary" />

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita os controles em modo
                visualização (checkboxes, radios, MonthPicker). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.graduationTypes)}>
                  <FormFieldLabel className="text-foreground">Tipo de formação</FormFieldLabel>
                  <CheckboxPillGroup
                    options={GRADUATION_OPTIONS}
                    selected={values.graduationTypes}
                    onToggle={onToggleGraduationType}
                    testIdPrefix="job-graduation-type"
                  />
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.graduationTypes}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <div className="flex flex-col gap-5 md:grid md:grid-cols-2 md:gap-5">
                  <FormField error={Boolean(errors.dateMin)}>
                    <FormFieldLabel className="text-foreground">
                      Data mínima de formação
                    </FormFieldLabel>
                    <MonthPicker
                      value={values.dateMin}
                      hasError={Boolean(errors.dateMin)}
                      onChange={onDateMinChange}
                      placeholder="Selecione mês e ano"
                      data-testid="job-graduation-date-min"
                    />
                    <FormFieldHint>Campo opcional</FormFieldHint>
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.dateMin}
                    </FormFieldError>
                  </FormField>

                  <FormField error={Boolean(errors.dateMax)}>
                    <FormFieldLabel className="text-foreground">
                      Data máxima de formação
                    </FormFieldLabel>
                    <MonthPicker
                      value={values.dateMax}
                      hasError={Boolean(errors.dateMax)}
                      onChange={onDateMaxChange}
                      placeholder="Selecione mês e ano"
                      data-testid="job-graduation-date-max"
                    />
                    <FormFieldHint>Campo opcional</FormFieldHint>
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.dateMax}
                    </FormFieldError>
                  </FormField>
                </div>
              </BlockedEditTooltip>

              {showCourseLevel ? (
                <BlockedEditTooltip enabled={readOnly}>
                  <FormField error={Boolean(errors.courseRequirementLevel)}>
                    <FormFieldLabel className="text-foreground">
                      Nível de abertura do requisito de cursos
                    </FormFieldLabel>
                    <RadioCardGroup
                      value={values.courseRequirementLevel}
                      onValueChange={(value) =>
                        onCourseRequirementLevelChange(value as CourseRequirementLevel)
                      }
                      className="flex flex-row gap-3"
                    >
                      <RadioCardItem
                        value="qualquer-curso"
                        className="group flex-1 px-4 py-4"
                        data-testid="job-graduation-course-level-qualquer-curso"
                      >
                        Qualquer curso
                      </RadioCardItem>
                      <RadioCardItem
                        value="quero-especificar"
                        className="group flex-1 px-4 py-4"
                        data-testid="job-graduation-course-level-quero-especificar"
                      >
                        Quero especificar
                      </RadioCardItem>
                    </RadioCardGroup>
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.courseRequirementLevel}
                    </FormFieldError>
                  </FormField>
                </BlockedEditTooltip>
              ) : null}
            </fieldset>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-graduation-back"
              >
                Voltar
              </Button>
              <Button
                type="submit"
                size="lg"
                loading={isSubmitting}
                disabled={!canSubmit}
                data-testid="job-graduation-submit"
              >
                Avançar
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

interface CheckboxPillGroupProps<T extends string> {
  options: readonly { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
  testIdPrefix: string;
}

function CheckboxPillGroup<T extends string>({
  options,
  selected,
  onToggle,
  testIdPrefix,
}: CheckboxPillGroupProps<T>) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option.value);
        return (
          <label
            key={option.value}
            className={cn(
              'group inline-flex cursor-pointer select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
              isChecked
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-foreground hover:border-primary'
            )}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => onToggle(option.value)}
              className="size-4"
              data-testid={`${testIdPrefix}-${option.value}`}
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
